// src/pages/RecipeDirectory.jsx
import './RecipeDirectory.css';
import React, { useEffect, useMemo, useState } from 'react';
import { recipeDatabase, cuisines } from '../data/recipeData';
import { getGameStats, awardXp, markRecipeCooked } from '../utils';
import RecipeModal from '../components/RecipeModal';

const normalizeCuisine = (c) => (c === "✨ Fusion" ? "Fusion" : c);

const matchesQuery = (recipe, q) => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    recipe.name.toLowerCase().includes(needle) ||
    recipe.cuisine.toLowerCase().includes(needle) ||
    recipe.ingredients.some((x) => x.toLowerCase().includes(needle)) ||
    recipe.sauces.some((x) => x.toLowerCase().includes(needle))
  );
};

// Generate concise description from instructions
const getRecipeDescription = (recipe) => {
  const sentences = recipe.instructions.split('. ');
  // Return first 2 sentences for a concise description
  if (sentences.length >= 2) {
    return sentences.slice(0, 2).join('. ') + '.';
  }
  return sentences[0] + (sentences[0].endsWith('.') ? '' : '.');
};

const RecipeDirectory = () => {
  const [activeCuisine, setActiveCuisine] = useState("All");
  const [query, setQuery] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [cookedSet, setCookedSet] = useState(new Set());
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  useEffect(() => {
    const loadStats = () => {
      const stats = getGameStats();
      setCookedSet(new Set(stats.cooked.map((r) => r.name)));
    };

    loadStats();

    // Listen for updates from other components
    const handleUpdate = () => loadStats();
    window.addEventListener('gameStatsUpdated', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('gameStatsUpdated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);

  const filteredByCuisine = useMemo(() => {
    const want = normalizeCuisine(activeCuisine);
    return recipeDatabase.filter(
      (r) => activeCuisine === "All" || normalizeCuisine(r.cuisine) === want
    );
  }, [activeCuisine]);

  const visibleRecipes = useMemo(
    () => filteredByCuisine.filter((r) => matchesQuery(r, query)),
    [filteredByCuisine, query]
  );

  const cuisinesToShow = useMemo(() => {
    if (activeCuisine !== "All") return [normalizeCuisine(activeCuisine)];
    return cuisines
      .filter((c) => c !== "All")
      .map(normalizeCuisine);
  }, [activeCuisine]);

  const cookedBadge = (name) => (cookedSet.has(name) ? "rd-badge done" : "rd-badge");

  const cookThis = (recipe) => {
    awardXp(recipe.points);
    markRecipeCooked({ name: recipe.name, cuisine: recipe.cuisine });

    const stats = getGameStats();
    setCookedSet(new Set(stats.cooked.map((r) => r.name)));

    const id = slugId(recipe);
    setHighlightId(id);
    setTimeout(() => setHighlightId(null), 1200);
    
    // Notify other components
    window.dispatchEvent(new Event('gameStatsUpdated'));
  };

  const slugId = (r) =>
    `r-${normalizeCuisine(r.cuisine).toLowerCase()}-${r.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")}`;

  const surpriseMe = () => {
    if (visibleRecipes.length === 0) return;
    const pick = visibleRecipes[Math.floor(Math.random() * visibleRecipes.length)];
    const id = slugId(pick);
    setHighlightId(id);
    setTimeout(() => setHighlightId(null), 1500);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  return (
    <div className="rd-shell">
      {/* TOOLBAR */}
      <section className="rd-toolbar">
        <div className="rd-tabs">
          {cuisines.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCuisine(c)}
              className={`rd-tab ${activeCuisine === c ? "active" : ""}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="rd-actions">
          <input
            className="rd-search"
            placeholder="SEARCH BY NAME / INGREDIENT / SAUCE"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="rd-btn rd-surprise" onClick={surpriseMe}>
            🎲 SURPRISE ME
          </button>
        </div>
      </section>

      {/* LIST / GROUPS */}
      {cuisinesToShow.map((group) => {
        const list =
          activeCuisine === "All"
            ? visibleRecipes.filter((r) => normalizeCuisine(r.cuisine) === group)
            : visibleRecipes;

        if (activeCuisine === "All" && list.length === 0) return null;

        return (
          <section key={group} className="rd-section">
            {activeCuisine === "All" && (
              <>
                <h2 className="rd-h2">{group}</h2>
                <div className="rd-divider" />
              </>
            )}

            <div className="rd-grid">
              {list.length === 0 ? (
                <div className="rd-empty">No recipes found.</div>
              ) : (
                list.map((r) => (
                  <article
                    key={r.name}
                    id={slugId(r)}
                    className={`rd-card ${highlightId === slugId(r) ? "pulse" : ""}`}
                  >
                    <div className="rd-card-head">
                      <h3 className="rd-title">
                        {r.name}
                        {r.special && <span className="rd-star">★</span>}
                      </h3>
                      <div className="rd-meta">
                        <span className="rd-pill">{r.time}</span>
                        <span
                          className={`rd-pill ${
                            r.difficulty === "Easy"
                              ? "easy"
                              : r.difficulty === "Medium"
                              ? "med"
                              : "hard"
                          }`}
                        >
                          {r.difficulty}
                        </span>
                        <span className="rd-pill xp">+{r.points} PTS</span>
                        <span className={cookedBadge(r.name)}>
                          {cookedSet.has(r.name) ? "COOKED" : "NEW"}
                        </span>
                      </div>
                    </div>

                    {/* Show concise description - first 2 sentences */}
                    <p className="rd-instructions">{getRecipeDescription(r)}</p>

                    <div className="rd-tags">
                      {r.ingredients.map((ing) => (
                        <span key={`i-${r.name}-${ing}`} className="rd-tag ing">
                          {ing}
                        </span>
                      ))}
                      {r.sauces.map((s) => (
                        <span key={`s-${r.name}-${s}`} className="rd-tag sauce">
                          {s}
                        </span>
                      ))}
                    </div>

                    {/* Two buttons: View Recipe and Cook This */}
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setSelectedRecipe(r)} 
                        className="flex-1 rd-btn"
                        style={{
                          background: '#22d3ee',
                          color: '#000',
                          borderColor: '#22d3ee'
                        }}
                      >
                        📖 VIEW RECIPE
                      </button>
                      <button 
                        className="flex-1 rd-btn rd-cook" 
                        onClick={() => cookThis(r)}
                      >
                        COOK THIS! (+{r.points} PTS)
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        );
      })}

      {/* Recipe Modal */}
      <RecipeModal 
        recipe={selectedRecipe}
        onClose={() => setSelectedRecipe(null)}
        onCook={cookThis}
      />
    </div>
  );
};

export default RecipeDirectory;
